import React, { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { useParams } from 'react-router';
import { apiGetDrawingsVisible, apiSetDrawingsVisible } from 'API/lotteryApi';
import type { ApiError } from 'API/apiClient';
import { useAppDispatch } from 'store/store';
import { addAlert } from 'store/reducers/alertReducer';
import { PageHeader } from 'components/Layout/PageHeader';
import { CustomSelect } from 'components/FormFields/CustomSelect/CustomSelect';
import RedButton from 'Styled/RedButton';

const DRAWING_COUNT_OPTIONS = Array.from({ length: 12 }, (_, index) => ({ id: index + 1, title: String(index + 1) }));

/**
 * "Allgemein" — the lottery-wide settings of the old `Misc` page. It currently
 * holds one setting: how many drawings the public client API hands out
 * (`drawings_visible`).
 */
export const GeneralPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const [storedValue, setStoredValue] = useState('');
  const [value, setValue] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!lotteryId) return;

    let current = true;
    apiGetDrawingsVisible(Number(lotteryId))
      .then(({ drawingsVisible }) => {
        if (!current) return;
        setStoredValue(String(drawingsVisible));
        setValue(String(drawingsVisible));
      })
      .catch((error: ApiError) => {
        if (current) setLoadError(error.message);
      });

    return () => {
      current = false;
    };
  }, [lotteryId]);

  const save = async () => {
    if (!lotteryId || !value) return;
    setSaving(true);
    try {
      const { drawingsVisible } = await apiSetDrawingsVisible(Number(lotteryId), Number(value));
      setStoredValue(String(drawingsVisible));
      setValue(String(drawingsVisible));
      dispatch(addAlert({ message: 'Die Anzahl wurde gespeichert.', severity: 'success' }));
    } catch {
      dispatch(addAlert({ message: 'Die Anzahl konnte nicht gespeichert werden.', severity: 'warning' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader title="Allgemein" />

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {!loadError && storedValue && (
        <Box sx={{ maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ maxWidth: 400 }}>
            <CustomSelect name="drawings-visible" label="Ziehungen" value={value} onChange={setValue} options={DRAWING_COUNT_OPTIONS} helperText="Wieviele Ziehungen sollen vom Kunden zurückverfolgt werden können?"/>
          </Box>
          <Box sx={{ marginTop: 2 }}>
            <RedButton type="button" disabled={saving || !value || value === storedValue} onClick={() => void save()}>
              Speichern
            </RedButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};
